import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, Users, Image, Lock, Globe, Info } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  'D&D', 'Chess', 'Catan', 'Magic: The Gathering',
  'Pokemon', 'Yu-Gi-Oh!', 'Warhammer', 'Monopoly',
  'Ticket to Ride', 'Scrabble', 'Risk', 'Other'
];

export default function CreateEventPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    game_type: '',
    location: '',          // Exact address (private when requires_approval=true)
    location_city: '',     // Public
    location_zip: '',      // Public
    date: '',
    time: '',
    max_players: 4,
    image: '',
    requires_approval: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const togglePrivacy = (isPrivate) => {
    setFormData(prev => ({ ...prev, requires_approval: isPrivate }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.game_type) {
      toast.error('Please select a game type');
      return;
    }
    if (!formData.location_city.trim()) {
      toast.error('Please add the city — players need it to find your event');
      return;
    }
    if (formData.requires_approval && !formData.location.trim()) {
      toast.error('Add the exact address — only approved players will see it');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/sessions`, formData, { withCredentials: true });
      toast.success('Session created!');
      navigate(`/events/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container" data-testid="create-session-page">
      {/* Header */}
      <header className="p-6 pb-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Create Session</h1>
          <p className="text-[#71717A] text-sm">Host your game night</p>
        </div>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-6 pb-32 space-y-5" data-testid="create-session-form">
        {/* Game Type Selection */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-3">Game Type *</label>
          <div className="flex flex-wrap gap-2">
            {GAME_TYPES.map((game) => (
              <GamePill key={game} game={game} selected={formData.game_type === game} onClick={() => setFormData(prev => ({ ...prev, game_type: game }))} />
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Session Title *</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} className="input-field" placeholder="e.g., Friday Night D&D Campaign" required data-testid="session-title-input" />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Description *</label>
          <textarea name="description" value={formData.description} onChange={handleChange} className="input-field min-h-[100px] resize-none" placeholder="Tell players what to expect..." required data-testid="session-description-input" />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
              <Clock size={14} className="inline mr-1" /> Date *
            </label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} className="input-field" required data-testid="session-date-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Time *</label>
            <input type="time" name="time" value={formData.time} onChange={handleChange} className="input-field" required data-testid="session-time-input" />
          </div>
        </div>

        {/* ============ LOCATION PRIVACY TOGGLE ============ */}
        <div className="rounded-2xl bg-[#0F0F16] border border-white/5 p-4 space-y-3" data-testid="privacy-toggle-section">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={16} weight="duotone" className="text-[#9D4CDD]" />
            <h3 className="text-sm font-semibold text-white">Event Privacy</h3>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => togglePrivacy(true)}
              className={`p-3 rounded-xl border text-left transition-all ${
                formData.requires_approval
                  ? 'border-[#9D4CDD]/60 bg-[#9D4CDD]/10'
                  : 'border-white/5 bg-[#0a0a12] hover:border-white/10'
              }`}
              data-testid="privacy-approval-required"
            >
              <Lock size={16} weight="duotone" className={formData.requires_approval ? 'text-[#9D4CDD]' : 'text-[#71717A]'} />
              <p className={`mt-2 text-sm font-semibold ${formData.requires_approval ? 'text-white' : 'text-[#A1A1AA]'}`}>Approval required</p>
              <p className="text-[10px] text-[#71717A] mt-0.5">Address hidden until you approve</p>
            </button>
            <button
              type="button"
              onClick={() => togglePrivacy(false)}
              className={`p-3 rounded-xl border text-left transition-all ${
                !formData.requires_approval
                  ? 'border-[#39FF14]/60 bg-[#39FF14]/10'
                  : 'border-white/5 bg-[#0a0a12] hover:border-white/10'
              }`}
              data-testid="privacy-open-event"
            >
              <Globe size={16} weight="duotone" className={!formData.requires_approval ? 'text-[#39FF14]' : 'text-[#71717A]'} />
              <p className={`mt-2 text-sm font-semibold ${!formData.requires_approval ? 'text-white' : 'text-[#A1A1AA]'}`}>Open to all</p>
              <p className="text-[10px] text-[#71717A] mt-0.5">Anyone can join instantly</p>
            </button>
          </div>

          <div className="flex items-start gap-2 px-1">
            <Info size={12} className="text-[#71717A] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#71717A] leading-relaxed">
              {formData.requires_approval
                ? 'Recommended for home games. Players see only your city & zip until you approve their request.'
                : 'Best for public venues, cafés, and shops. Your full address is visible to all players.'}
            </p>
          </div>
        </div>

        {/* ============ LOCATION FIELDS ============ */}
        <div className="space-y-3">
          {/* City + Zip (always public) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                <Globe size={14} className="inline mr-1" /> City *
              </label>
              <input
                type="text"
                name="location_city"
                value={formData.location_city}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g., Brooklyn"
                required
                data-testid="session-city-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">ZIP / Postal</label>
              <input
                type="text"
                name="location_zip"
                value={formData.location_zip}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g., 11201"
                data-testid="session-zip-input"
              />
            </div>
          </div>

          {/* Exact address */}
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2 flex items-center gap-1">
              <MapPin size={14} className="inline" />
              Exact Address {formData.requires_approval ? '*' : '(optional)'}
              {formData.requires_approval && (
                <span className="ml-auto text-[10px] text-[#9D4CDD] flex items-center gap-1">
                  <Lock size={10} weight="fill" /> Private
                </span>
              )}
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="input-field"
              placeholder="e.g., 123 Main St, Apt 4B"
              required={formData.requires_approval}
              data-testid="session-location-input"
            />
            <p className="text-[10px] text-[#71717A] mt-1.5">
              {formData.requires_approval
                ? 'Shown only after you approve a player\'s join request'
                : 'Visible to everyone who views this event'}
            </p>
          </div>
        </div>

        {/* Max Players */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
            <Users size={14} className="inline mr-1" /> Max Players *
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              name="max_players"
              value={formData.max_players}
              onChange={handleChange}
              min="2"
              max="20"
              className="flex-1 h-2 bg-[#181824] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#9D4CDD] [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(157,76,221,0.5)]"
              data-testid="max-players-slider"
            />
            <span className="text-white font-bold text-lg w-8 text-center">{formData.max_players}</span>
          </div>
        </div>

        {/* Image URL */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
            <Image size={14} className="inline mr-1" /> Cover Image URL (optional)
          </label>
          <input type="url" name="image" value={formData.image} onChange={handleChange} className="input-field" placeholder="https://..." data-testid="session-image-input" />
        </div>

        {/* Submit */}
        <motion.button type="submit" disabled={loading} className="btn-primary w-full mt-6" whileTap={{ scale: 0.98 }} data-testid="create-session-submit">
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Creating...
            </span>
          ) : (
            'Create Session'
          )}
        </motion.button>
      </form>

      <BottomNav />
    </div>
  );
}
